import { useState, useRef, useCallback, useEffect } from 'react';
import { MIN_ZOOM, MAX_ZOOM } from './orgChartHelpers';

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

/**
 * Viewport state for the chart: a single `translate(x, y) scale(k)` applied to
 * one wrapper, driven by pointer drag and the wheel.
 *
 * Zoom keeps the point under the cursor fixed — zooming toward a corner and
 * having the content slide out from under you is the usual failing of a naive
 * `scale` change, and it makes a large chart genuinely hard to navigate.
 *
 * Pointer Events (not mouse events) so a trackpad, mouse and touch all work, and
 * `setPointerCapture` so a fast drag that leaves the element does not strand the
 * canvas mid-pan.
 */
const usePanZoom = ({ viewportRef, contentSize }) => {
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const dragStart = useRef(null);
  // Distance travelled in the current gesture. Cards cover most of the canvas, so
  // panning has to work when you grab one — which means a click on a card must be
  // told apart from the end of a drag, or every pan would also open somebody's
  // record.
  const dragDistance = useRef(0);

  const zoomAround = useCallback((factor, clientX, clientY) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;

    setTransform((prev) => {
      const k = clampZoom(prev.k * factor);
      if (k === prev.k) return prev;

      // Anchor: keep the content point under the cursor where it is.
      const px = (clientX ?? rect.left + rect.width / 2) - rect.left;
      const py = (clientY ?? rect.top + rect.height / 2) - rect.top;
      const ratio = k / prev.k;

      return { k, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio };
    });
  }, [viewportRef]);

  const zoomIn = useCallback(() => zoomAround(1.2), [zoomAround]);
  const zoomOut = useCallback(() => zoomAround(1 / 1.2), [zoomAround]);

  /** Scale the whole chart to fit, with a little breathing room. */
  const fitToScreen = useCallback(() => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect || !contentSize.width || !contentSize.height) return;

    const padding = 48;
    const k = clampZoom(
      Math.min(
        (rect.width - padding * 2) / contentSize.width,
        (rect.height - padding * 2) / contentSize.height,
        1,
      ),
    );

    setTransform({
      k,
      x: (rect.width - contentSize.width * k) / 2,
      y: padding,
    });
  }, [viewportRef, contentSize.width, contentSize.height]);

  /** Bring a content-space point to the middle of the viewport. */
  const centerOn = useCallback((contentX, contentY) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;

    setTransform((prev) => ({
      ...prev,
      x: rect.width / 2 - contentX * prev.k,
      y: rect.height / 2 - contentY * prev.k,
    }));
  }, [viewportRef]);

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0) return;

    // Reset BEFORE the opt-out checks: a control that skips panning still ends a
    // gesture, and leaving the previous drag's distance behind would make the
    // next click on it look like the tail of a drag and be swallowed.
    dragDistance.current = 0;

    // Dragging an object moves the object; dragging the background moves the
    // view. Cards carry data-orgchart-node and are handled by useNodeDrag, and
    // small controls (the expand toggle, the ⋮ menu) opt out entirely.
    if (event.target.closest('[data-orgchart-node]')) return;
    if (event.target.closest('[data-orgchart-nopan]')) return;

    dragStart.current = { pointerX: event.clientX, pointerY: event.clientY };
    setIsPanning(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event) => {
    if (!dragStart.current) return;

    const { pointerX, pointerY } = dragStart.current;
    const dx = event.clientX - pointerX;
    const dy = event.clientY - pointerY;
    dragStart.current = { pointerX: event.clientX, pointerY: event.clientY };
    dragDistance.current += Math.abs(dx) + Math.abs(dy);

    setTransform((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
  }, []);

  const endPan = useCallback((event) => {
    dragStart.current = null;
    setIsPanning(false);
    event?.currentTarget?.releasePointerCapture?.(event.pointerId);
  }, []);

  /**
   * Wheel handling is attached imperatively with `{ passive: false }`: React's
   * onWheel is registered as passive, so preventDefault() there is ignored and
   * the browser zooms the whole page instead of the chart.
   */
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();

      if (event.ctrlKey || event.metaKey || Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        zoomAround(event.deltaY < 0 ? 1.1 : 1 / 1.1, event.clientX, event.clientY);
        return;
      }

      setTransform((prev) => ({ ...prev, x: prev.x - event.deltaX, y: prev.y - event.deltaY }));
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [viewportRef, zoomAround]);

  /**
   * True when the gesture that just ended was a drag rather than a click. 4px of
   * slack because a real click on a trackpad almost always moves a pixel or two.
   */
  const wasDragged = useCallback(() => dragDistance.current > 4, []);

  return {
    transform,
    isPanning,
    wasDragged,
    zoomIn,
    zoomOut,
    fitToScreen,
    centerOn,
    resetZoom: useCallback(() => setTransform((prev) => ({ ...prev, k: 1 })), []),
    panHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPan,
      onPointerCancel: endPan,
    },
  };
};

export default usePanZoom;
